import React from "react";
import { ThemeProvider, DefaultTheme } from "styled-components";

declare module "styled-components" {
  export interface DefaultTheme {
    colors: {
      primary: string;
      primaryLight: string;
      primaryLightHover: string;
      secondary: string;
      secondaryLight: string;
      secondaryLightHover: string;
      black: string;
      white: string;
      green: string;
      red: string;
      darkRed: string;
      grey: string;
      pageBackground: string;
      primaryTextColor: string;
      backdropColor: string;
      disabled: string;
      error: string;
      errorLight: string;
      success: string;
      successLight: string;
      warning: string;
      warningLight: string;
      info: string;
      infoLight: string;
      text: string;
      muted: string;
      border: string;
      backdrop: string;
      background: string;
      gray50: string;
      gray100: string;
      gray200: string;
      gray300: string;
      gray400: string;
      gray500: string;
      gray600: string;
      gray700: string;
      gray800: string;
      gray900: string;
      mainPurpleColor: string;
      mainLinkColor: string;
      btnPrimaryBgColor: string;
      btnPrimaryBorderColor: string;
      btnPrimaryTextColor: string;
      btnPrimaryBgColorHover: string;
      btnPrimaryBorderColorHover: string;
      btnPrimaryTextColorHover: string;
      btnSecondaryBgColor: string;
      btnSecondaryBorderColor: string;
      btnSecondaryTextColor: string;
      btnSecondaryBgColorHover: string;
      btnSecondaryBorderColorHover: string;
      btnSecondaryTextColorHover: string;
      btnDisabledBgColor: string;
      btnDisabledBorderColor:string;
      btnDisabledTextColor: string;
      btnDisabledBgColorHover: string;
      btnDisabledBorderColorHover: string;
      btnDisabledTextColorHover: string;
    };
    fontSize: {
      xSmall: {
        desktop: string;
        mobile: string;
      };
      small: {
        desktop: string;
        mobile: string;
      };
      normal: {
        desktop: string;
        mobile: string;
      };
      large: {
        desktop: string;
        mobile: string;
      };
      cardTitle: {
        desktop: string;
        mobile: string;
      };
      icon: {
        desktop: string;
        mobile: string;
      };
    };
    fontWeight: {
      light: string;
      bolder: string;
      bold: string;
      normal: string;
      medium: string;
    };
    lineHeight: {
      desktop: string;
      mobile: string;
    };

    /* Legacy theme value */
    fontFamily: string;

    font: string;
    borderRadius: string;
    borderRadiusSmall: string;
    boxShadow: string;
    spacing: {
      xs: string;
      s: string;
      sm: string;
      m: string;
      ml: string;
      l: string;
      xl: string;
      xxl: string;
      xxxl: string;
      none: string;
    };
  }
}

export const defaultTheme: DefaultTheme = {
  colors: {
    primary: "#79D9D9",
    primaryLight: "#79d9d91c",
    primaryLightHover: "#79d9d96e",
    // primaryLightHover: "#ffe4f3",
    secondary: "#402680",
    secondaryLight: "#f7f3fa",
    secondaryLightHover: "#f0e7f6",
    black: "#000000",
    white: "#ffffff",
    green: "#27ae60",
    red: "#ff3131",
    // darkRed: "#bc006d", // dark pink
    darkRed: "#402680",
    grey: "#666",
    pageBackground: "#e4e5e7",
    primaryTextColor: "#333333",
    backdropColor: "rgba(0,0,0,0.6)",
    disabled: "#999999",
    text: "#1F2937",
    muted: "#9CA3AF",
    border: "#D1D5DB",
    error: "#EF4444",
    errorLight: "#fff4f4",
    success: "#10B981",
    successLight: "#f2fcf6",
    warning: "#F59E0B",
    warningLight: "#FFFBEB",
    info: "#3B82F6",
    infoLight: "#EFF6FF",
    backdrop: "rgba(0, 0, 0, 0.85)",
    background: "white",
    gray50: "#F9FAFB",
    gray100: "#F3F4F6",
    gray200: "#E5E7EB",
    gray300: "#D1D5DB",
    gray400: "#9CA3AF",
    gray500: "#6B7280",
    gray600: "#4B5563",
    gray700: "#374151",
    gray800: "#1F2937",
    gray900: "#111827",
    mainPurpleColor: "#402680",
    mainLinkColor: "#79D9D9",
    btnPrimaryBgColor:  "#402680", 
    btnPrimaryBorderColor: "#402680",
    btnPrimaryTextColor: "#ffffff",
    btnPrimaryBgColorHover: "#FFFFFF", 
    btnPrimaryBorderColorHover: "#402680",
    btnPrimaryTextColorHover: "#402680",
    btnSecondaryBgColor: "#402680",
    btnSecondaryBorderColor: "#402680",
    btnSecondaryTextColor: "#79D9D9",
    btnSecondaryBgColorHover: "#FFFFFF", 
    btnSecondaryBorderColorHover: "#79D9D9",
    btnSecondaryTextColorHover: "#79D9D9",
    btnDisabledBgColor: "#79D9D9", 
    btnDisabledBorderColor:"#402680",
    btnDisabledTextColor: "#402680",
    btnDisabledBgColorHover: "#9CA3AF", 
    btnDisabledBorderColorHover: "#9CA3AF",
    btnDisabledTextColorHover: "#fff",
  },
  fontFamily: "'Komet', sans-serif",
  fontSize: {
    xSmall: {
      desktop: "0.61em",
      mobile: "0.5em",
    },
    small: {
      desktop: "0.8em",
      mobile: "0.7em",
    },
    normal: {
      desktop: "1em",
      mobile: "0.9em",
    },
    large: {
      desktop: "2.25em",
      mobile: "2em",
    },
    cardTitle: {
      desktop: "1em",
      mobile: "0.9em",
    },
    icon: {
      desktop: "1.25em",
      mobile: "1em",
    },
  },
  fontWeight: {
    light: "200",
    normal: "400",
    medium: "600",
    bolder: "700",
    bold: "900",
  },
  lineHeight: {
    desktop: "1.7em",
    mobile: "1.5em",
  },
  borderRadius: "8px",
  borderRadiusSmall: "4px",
  boxShadow: "rgb(220, 220, 220) 0px 0px 15px",
  spacing: {
    xs: "2.5px",
    s: "5px",
    sm: "10px",
    m: "15px",
    ml: "20px",
    l: "25px",
    xl: "30px",
    xxl: "35px",
    xxxl: "40px",
    none: "0px",
  },

  font: "'Komet', sans-serif",
};

const Theme = ({ children }) => (
  <ThemeProvider theme={defaultTheme}>{children}</ThemeProvider>
);

export default Theme;
